import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import axios from "axios";
import languagesData from "../assets/languages.json";
import { getCampaignFlowById } from "../services/api";
function BriefDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [states, setStates] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [campaign, setCampaign] = useState({});
  const [errors, setErrors] = useState({});
  const [readOnlyFields, setReadOnlyFields] = useState([]);
  
  const [formData, setFormData] = useState({
    team: "brand_team",
    brand: "",
    product: "",
    objective: "",
    message: "",
    keyProblem: "",
    consumerInsights: "",
    campaignPeriod: "",
    budget: "",
    market: "",
    gender: "",
    age: "",
    languages: "",
    personaKeywords: "",
    audiencerPersona: "",
    heroMessage: "",
    supportPoints: "",
    cta: "",
    productAvailability: "",
    dos: "",
    donts: "",
    talkObjective: "",
    bestResults: "",
    colourScheme: ""
  });

  useEffect(() => {
    fetchBrands();
    fetchProducts();
    if (id) fetchCampaignDetails(id);
    fetchStates("India");
    fetchLanguages();
  }, [id]);

  useEffect(() => {
    if (campaign && campaign.brandTeam) {
      const updatedData = {
        brand: campaign.brand?._id || "",
        product: campaign.product?._id || "",
        objective: campaign.brandTeam?.objective || "",
        message: campaign.brandTeam?.message || "",
        keyProblem: campaign.brandTeam?.keyProblem || "",
        consumerInsights: campaign.brandTeam?.consumerInsights || "",
        campaignPeriod: campaign.brandTeam?.campaignPeriod || "",
        budget: campaign.brandTeam?.budget || "",
        market: campaign.brandTeam?.market || "",
        gender: campaign.brandTeam?.gender || "",
        age: campaign.brandTeam?.age || "",
        languages: campaign.brandTeam?.languages || "",
        personaKeywords: campaign.brandTeam?.personaKeywords || "",
        audiencerPersona: campaign.brandTeam?.audiencerPersona || "",
        heroMessage: campaign.brandTeam?.heroMessage || "",
        supportPoints: campaign.brandTeam?.supportPoints || "",
        productAvailability: campaign.brandTeam?.productAvailability || "",
        cta: campaign.brandTeam?.cta || "",
        dos: campaign.brandTeam?.dos || "",
        donts: campaign.brandTeam?.donts || "",
        talkObjective: campaign.brandTeam?.talkObjective || "",
        bestResults: campaign.brandTeam?.bestResults || "",
        colourScheme: campaign.brandTeam?.colourScheme || ""
      };
      setFormData(updatedData);
      setReadOnlyFields(Object.keys(updatedData));
    }
  }, [campaign]);
  const isReadOnly = (fieldName) => readOnlyFields.includes(fieldName);

  const fetchBrands = async () => {
    try {
      const { default: api } = await import("../services/api");
      const response = await api.get("/brands");
      setBrands(response.data || []);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const { default: api } = await import("../services/api");
      const response = await api.get("/products");
      setProducts(response.data || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  const fetchCampaignDetails = async (id) => {
    try {
      const { default: api } = await import("../services/api");
      const response = await api.get(`/campaign/${id}`);
      setCampaign(response.data?.data || {});
    } catch (error) {
      console.error("Failed to fetch Campaign Details:", error);
    }
  };

  const fetchStates = async (country) => {
    try {
      const response = await axios.post(
        "https://countriesnow.space/api/v0.1/countries/states",
        { country },
      );
      setStates(response.data?.data?.states || []);
    } catch (error) {
      console.error("Failed to fetch States:", error);
    }
  };

  const fetchLanguages = () => {
    try {
      setLanguages(languagesData.google_supported_languages || []);
    } catch (error) {
      console.error("Failed to fetch Languages:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.brand.trim()) newErrors.brand = "Brand is required";
    if (!formData.product.trim()) newErrors.product = "Product is required";
    if (!formData.objective.trim())
      newErrors.objective = "Campaign objective is required";
    if (!formData.budget.trim()) newErrors.budget = "Budget is required";
    if (!formData.market.trim()) newErrors.market = "Market is required";
    if (!formData.gender.trim()) newErrors.gender = "Gender is required";
    if (!formData.age.trim()) newErrors.age = "Age is required";
    if (!formData.heroMessage.trim())
      newErrors.heroMessage = "Hero message is required";
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const { default: api } = await import("../services/api");
      await api.put(`/campaign/flow/${id}`, formData);
      setReadOnlyFields(Object.keys(formData));
      fetchCampaignDetails(id);
      navigate('/marketing');
    } catch (error) {
      console.error("Failed to submit brief details:", error);
    }
  };

  const handleReset = () => {
    setFormData({
      // brandName: '',
      // productName: '',
      // campaignName: '',
      team: "brand_team",
      brand: "",
      market: "",
      objective: "",
      message: "",
      keyProblem: "",
      consumerInsights: "",
      campaignPeriod: "",
      budget: "",
      market: "",
      gender: "",
      age: "",
      languages: "",
      personaKeywords: "",
      audiencerPersona: "",
      heroMessage: "",
      supportPoints: "",
      cta: "",
      productAvailability: "",
      dos: "",
      donts: "",
      talkObjective: "",
      bestResults: "",
      colourScheme: ""
    });
    setErrors({});
  };

  return (
    <Box sx={{ p: 3 }}>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
            Brief Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color={'text.secondary'} sx={{ mb: 2}}>
              Campaign ID : <strong style={{ color: '#2e7d32' }}> {campaign?.campaignId ?  campaign?.campaignId : '-'} </strong>
            </Typography>
            <Typography variant="body2" color={'text.secondary'} sx={{ mb: 2}}>
              Campaign Name : <strong style={{ color: '#2e7d32' }}> {campaign?.campaignName} </strong>
            </Typography>
          </Box>
          {/* <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Brand name"
                size="small"
                value={formData.brandName}
                onChange={(e) => handleChange('brandName', e.target.value)}
                placeholder="Brand name"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Product name"
                size="small"
                value={formData.productName}
                onChange={(e) => handleChange('productName', e.target.value)}
                placeholder="Product name"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Campaign name"
                size="small"
                value={formData.campaignName}
                onChange={(e) => handleChange('campaignName', e.target.value)}
                placeholder="Campaign name"
              /> 
            </Grid>
          </Grid> */}

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Campaign Input
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" error={!!errors.brand}>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={formData.brand}
                  label="Brand"
                  onChange={(e) => handleChange("brand", e.target.value)}
                  inputProps={{ readOnly: isReadOnly("brand") }}
                >
                  <MenuItem value="">Select Brand</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand._id} value={brand._id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.brand && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.brand}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" error={!!errors.product}>
                <InputLabel>Product</InputLabel>
                <Select
                  value={formData.product}
                  label="Product"
                  onChange={(e) => handleChange("product", e.target.value)}
                  inputProps={{ readOnly: isReadOnly("product") }}
                >
                  <MenuItem value="">Select Product</MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product._id} value={product._id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.product && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.product}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Campaign objective"
                size="small"
                value={formData.objective}
                onChange={(e) => handleChange("objective", e.target.value)}
                InputProps={{ readOnly: isReadOnly("objective") }}
                placeholder="Objective"
                error={!!errors.objective}
                helperText={errors.objective}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Campaign message to carry"
                size="small"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                InputProps={{ readOnly: isReadOnly("message") }}
                placeholder="Message"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Key problem being solved"
                size="small"
                value={formData.keyProblem}
                onChange={(e) => handleChange("keyProblem", e.target.value)}
                InputProps={{ readOnly: isReadOnly("keyProblem") }}
                placeholder="Problem"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Consumer insights"
                size="small"
                value={formData.consumerInsights}
                onChange={(e) =>
                  handleChange("consumerInsights", e.target.value)
                }
                InputProps={{ readOnly: isReadOnly("consumerInsights") }}
                placeholder="Consumer insights"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {/* <TextField
                fullWidth
                label="Campaign period"
                size="small"
                value={formData.campaignPeriod}
                onChange={(e) => handleChange('campaignPeriod', e.target.value)}
                placeholder="Campaign period"
              /> */}
              <FormControl fullWidth size="small">
                {/* <FormControl fullWidth size="small" error={!!errors.campaignPeriod}> */}
                <InputLabel>Campaign period</InputLabel>
                <Select
                  value={formData.campaignPeriod}
                  label="Campaign period"
                  onChange={(e) =>
                    handleChange("campaignPeriod", e.target.value)
                  }
                  inputProps={{ readOnly: isReadOnly("campaignPeriod") }}
                >
                  <MenuItem value="">Select Campaign period</MenuItem>

                  <MenuItem key={"q1"} value={"q1"}>
                    {"Q1"}
                  </MenuItem>
                  <MenuItem key={"q2"} value={"q2"}>
                    {"Q2"}
                  </MenuItem>
                  <MenuItem key={"q3"} value={"q3"}>
                    {"Q3"}
                  </MenuItem>
                  <MenuItem key={"q4"} value={"q4"}>
                    {"Q4"}
                  </MenuItem>
                </Select>
                {/* {errors.campaignPeriod && <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{errors.campaignPeriod}</Typography>} */}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Budget"
                size="small"
                type="number"
                value={formData.budget}
                onChange={(e) => handleChange("budget", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{ readOnly: isReadOnly("budget") }}
                placeholder="Budget"
                error={!!errors.budget}
                helperText={errors.budget}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Target Audience Input
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              {/* <TextField
                fullWidth
                label="Market(State)"
                size="small"
                value={formData.market}
                onChange={(e) => handleChange('market', e.target.value)}
                placeholder="Market"
                error={!!errors.market}
                helperText={errors.market}
              /> */}
              <FormControl fullWidth size="small" error={!!errors.market}>
                <InputLabel>Market(State)</InputLabel>
                <Select
                  value={formData.market}
                  label="Market(State)"
                  onChange={(e) => handleChange("market", e.target.value)}
                  inputProps={{ readOnly: isReadOnly("market") }}
                >
                  <MenuItem value="">Select Market(State)</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.name} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.market && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.market}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              {/* <TextField
                fullWidth
                label="Gender"
                size="small"
                value={formData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                placeholder="Gender"
                error={!!errors.gender}
                helperText={errors.gender}
              /> */}
              <FormControl fullWidth size="small" error={!!errors.gender}>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => handleChange("gender", e.target.value)}
                  inputProps={{ readOnly: isReadOnly("gender") }}
                >
                  <MenuItem value="">Select Gender</MenuItem>

                  <MenuItem key={"male"} value={"male"}>
                    {"Male"}
                  </MenuItem>
                  <MenuItem key={"female"} value={"female"}>
                    {"Female"}
                  </MenuItem>
                </Select>
                {errors.gender && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Age"
                size="small"
                value={formData.age}
                onChange={(e) => handleChange("age", e.target.value)}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{ readOnly: isReadOnly("age") }}
                placeholder="Age"
                error={!!errors.age}
                helperText={errors.age}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {/* <TextField
                fullWidth
                label="Languages"
                size="small"
                value={formData.languages}
                onChange={(e) => handleChange('languages', e.target.value)}
                placeholder="Languages"
              /> */}
              <FormControl fullWidth size="small">
                {/* <FormControl fullWidth size="small" error={!!errors.languages}> */}
                <InputLabel>Languages</InputLabel>
                <Select
                  value={formData.languages}
                  label="Languages"
                  onChange={(e) => handleChange("languages", e.target.value)}
                  inputProps={{ readOnly: isReadOnly("languages") }}
                >
                  <MenuItem value="">Select Languages</MenuItem>
                  {languages.map((language) => (
                    <MenuItem key={language.name} value={language.name}>
                      {language.name}
                    </MenuItem>
                  ))}
                </Select>
                {/* {errors.languages && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.languages}
                  </Typography>
                )} */}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Persona keywords"
                size="small"
                value={formData.personaKeywords}
                onChange={(e) =>
                  handleChange("personaKeywords", e.target.value)
                }
                InputProps={{ readOnly: isReadOnly("personaKeywords") }}
                placeholder="Persona keywords"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Audiencer persona doc link"
                size="small"
                value={formData.audiencerPersona}
                onChange={(e) =>
                  handleChange("audiencerPersona", e.target.value)
                }
                InputProps={{ readOnly: isReadOnly("audiencerPersona") }}
                placeholder="Audiencer persona"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Core Communication
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Hero message"
                size="small"
                value={formData.heroMessage}
                onChange={(e) => handleChange("heroMessage", e.target.value)}
                InputProps={{ readOnly: isReadOnly("heroMessage") }}
                placeholder="Hero message"
                error={!!errors.heroMessage}
                helperText={errors.heroMessage}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="USP/Support points"
                size="small"
                value={formData.supportPoints}
                onChange={(e) => handleChange("supportPoints", e.target.value)}
                InputProps={{ readOnly: isReadOnly("supportPoints") }}
                placeholder="Support points"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Product availability"
                size="small"
                value={formData.productAvailability}
                onChange={(e) => handleChange("productAvailability", e.target.value)}
                InputProps={{ readOnly: isReadOnly("productAvailability") }}
                placeholder="Product availability"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="CTA"
                size="small"
                value={formData.cta}
                onChange={(e) => handleChange("cta", e.target.value)}
                InputProps={{ readOnly: isReadOnly("cta") }}
                placeholder="CTA"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
            DO'S & DONT'S
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Do's"
                size="small"
                multiline
                rows={4}
                value={formData.dos}
                onChange={(e) => handleChange("dos", e.target.value)}
                InputProps={{ readOnly: isReadOnly("dos") }}
                placeholder="Do's"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dont's"
                size="small"
                multiline
                rows={4}
                value={formData.donts}
                onChange={(e) => handleChange("donts", e.target.value)}
                InputProps={{ readOnly: isReadOnly("donts") }}
                placeholder="Dont's"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
            Other Inputs
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Talk about the objective"
                size="small"
                value={formData.talkObjective}
                onChange={(e) => handleChange("talkObjective", e.target.value)}
                InputProps={{ readOnly: isReadOnly("talkObjective") }}
                placeholder="Talk about the objective"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Best results when used once a week"
                size="small"
                value={formData.bestResults}
                onChange={(e) => handleChange("bestResults", e.target.value)}
                InputProps={{ readOnly: isReadOnly("bestResults") }}
                placeholder="Best results when used once a week"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Colour Scheme"
                size="small"
                value={formData.colourScheme}
                onChange={(e) => handleChange("colourScheme", e.target.value)}
                InputProps={{ readOnly: isReadOnly("colourScheme") }}
                placeholder="Colour Scheme"
              />
            </Grid>

          </Grid>

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            {!campaign.brandTeam && (
              <>
                <Button
                  variant="contained"
                  onClick={handleReset}
                  sx={{
                    px: 4,
                    py: 1,
                    bgcolor: "#1a1d29",
                    color: "#ffffff",
                    "&:hover": {
                      bgcolor: "#c5e86d",
                      color: "#1a1d29"
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ px: 4 }}
                >
                  Submit
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default BriefDetails;
